import { getJwtToken } from './cognito'

// `REACT_APP_API_URL` historically arrived with an inconsistent trailing
// slash (e.g. `.../dev/`) from the env-sync pipeline, which combined with
// route paths that start with `/api/...` to produce `.../dev//api/...`.
// API Gateway tolerated the double slash but it confused matchers and
// logs. Strip a single trailing slash defensively so callers can always
// pass a leading-slash path without worrying about the source format.
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')

/**
 * Authenticated fetch wrapper for the WPS Studio API.
 * Attaches the Cognito JWT as a Bearer token.
 */
export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getJwtToken()

  if (!token) {
    throw new ApiError(401, 'Not authenticated')
  }

  const url = `${API_BASE_URL}${path}`

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers
    }
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}
